import { useState } from "react";
import { useNotify } from "ra-core";
import { PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { clickToCall, phoneEnabled } from "./phoneApi";
import { useMyExtension } from "./useMyExtension";

interface CallButtonProps {
  number: string;
  contactId?: number;
}

/** Click-to-call: rings the user's own extension first, then dials the number through the office line. */
export function CallButton({ number, contactId }: CallButtonProps) {
  const notify = useNotify();
  const { extension } = useMyExtension();
  const [busy, setBusy] = useState(false);
  if (!phoneEnabled || !number) return null;

  const call = async () => {
    if (!extension) {
      notify("Set your extension in your profile before calling from the CRM", {
        type: "warning",
      });
      return;
    }
    setBusy(true);
    try {
      const result = await clickToCall(number, contactId);
      if (result.ok) {
        notify(
          `Calling ${number} — pick up extension ${result.from_ext ?? extension}`,
          { type: "info" },
        );
      } else {
        notify(result.message ?? "Call failed", { type: "error" });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-6"
      aria-label={`Call ${number}`}
      title={
        extension
          ? `Call from extension ${extension}`
          : "Set your extension in your profile"
      }
      disabled={busy}
      onClick={() => void call()}
    >
      <PhoneCall className="size-3.5" />
    </Button>
  );
}
